import type { SavedDestination } from "@solomon-apps/shared";
import {
    Text,
    textVariants
} from "@solomon-apps/shared/src/components/ui/text";
import { PlusIcon } from "@solomon-labs/design/components/icons";
import {
    Address,
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    InputGroupAddon,
    InputGroupText,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import {
    checkTypedDestination,
    findSavedDestination,
    matchesDestinationQuery,
    SAME_AS_SOURCE_LABEL,
    savedDestinationDisplayName,
    savedDestinationLabel
} from "@/pages/RewardDestinations/destinationInput";
import { ExchangeAddressWarning } from "@/pages/RewardDestinations/ExchangeAddressWarning";
import { formatAddressTier, useAddressFit } from "@/pages/shared/addressFit";
import { looksLikeExchangeAddress } from "@/pages/shared/format";
import { FormErrorRetry } from "@/pages/shared/FormErrorRetry";
import { DISABLED_VALUE_CLASS } from "@/pages/shared/lightbarField";

import { DESTINATION_PREFIX_CLASS } from "./DestinationLabelField";

/** The default option's id: rewards go to the position source. */
const SOURCE_UUID = "position-source";
/** The "Add a new destination address" row's id. */
const ADD_UUID = "add-destination";
/** The add row's copy. The typed text is already in the field above it. */
export const ADD_ROW_LABEL = "Add a new destination address";
/** Why the add row is off, on demand: the typed text is no Solana address. */
export const ADD_ROW_INVALID_REASON = "Paste a full Solana address to add it.";

/**
 * The value's type: the DS Input default (`small`). Every field in the Add a
 * Position and Add Rewards Destination flows reads at this one size, and so
 * does every option row.
 */
const VALUE_TYPE_CLASS = textVariants({ variant: "small" });
/** An address value keeps the numeric face, as `AddressField` does. */
const ADDRESS_VALUE_CLASS = "font-numeric tabular-nums";

/** A one-rung ladder that never shortens: the measure's full width. */
const FULL_ADDRESS = [{ minWidth: 0 }] as const;

/** Marks the stand-in for a typed-in address that isn't saved (yet). */
const DRAFT_UUID_PREFIX = "draft:";

/**
 * The item shape for a typed-in address: what the field shows while it
 * saves, or after the save failed. `name === address` is the saved
 * "no label" convention.
 */
function draftDestination(address: string): SavedDestination {
    return {
        uuid: `${DRAFT_UUID_PREFIX}${address}`,
        name: address,
        address,
        createdAt: "",
        updatedAt: ""
    };
}

/**
 * The default option, "Same as Position Source", in the item shape. It is a
 * real, selected-by-default choice (not placeholder text), and picking it is
 * the empty destination (`""`), which the submit payload resolves to the
 * source. Its name is a label, so search matches "same", "position source"
 * or the source address like any saved destination.
 */
function sourceDestination(address: string, name: string): SavedDestination {
    return {
        uuid: SOURCE_UUID,
        name,
        address,
        createdAt: "",
        updatedAt: ""
    };
}

/** The add row in the item shape, carrying the typed text as its address
 *  (what the field keeps showing if Base UI writes the pick back). */
function addDestination(typed: string): SavedDestination {
    return {
        uuid: ADD_UUID,
        name: ADD_ROW_LABEL,
        address: typed,
        createdAt: "",
        updatedAt: ""
    };
}

function isDraftDestination(destination: SavedDestination): boolean {
    return destination.uuid.startsWith(DRAFT_UUID_PREFIX);
}

/**
 * The full address, unrendered, in the same face as the `Address` beside
 * it: its natural width is what `useAddressFit` fits against. Clipped to
 * nothing so it can never widen the page.
 */
function AddressMeasure({
    address,
    measureRef
}: {
    address: string;
    measureRef: React.RefObject<HTMLSpanElement | null>;
}): React.ReactElement {
    return (
        <span
            aria-hidden
            className="pointer-events-none invisible absolute top-0 left-0 size-0 overflow-hidden"
        >
            <span ref={measureRef} className="block w-max">
                <Address
                    value={address}
                    tiers={FULL_ADDRESS}
                    showTitle={false}
                    textVariant="small"
                    mono={false}
                    className={`${ADDRESS_VALUE_CLASS} w-max max-w-none`}
                />
            </span>
        </span>
    );
}

/**
 * One option, on one line: the label at the left (`small`, truncating only
 * past three fifths of the row), the address at the right, beside the check,
 * as the DS `Address` in the same variant. The address gets whatever the
 * label leaves: it shows in full when that fits and shortens in the middle,
 * per row, when it doesn't (`useAddressFit` on its own box). An unlabeled
 * destination is just its address, at the right.
 */
function DestinationOption({
    destination
}: {
    destination: SavedDestination;
}): React.ReactElement {
    const boxRef = useRef<HTMLSpanElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const label = savedDestinationLabel(destination);
    const tier = useAddressFit(destination.address, boxRef, measureRef);
    return (
        <span className="flex min-w-0 flex-1 items-baseline gap-2">
            {label ? (
                <Text
                    variant="small"
                    render={<span />}
                    className="max-w-3/5 shrink-0 truncate text-foreground-base"
                >
                    {label}
                </Text>
            ) : null}
            <span
                ref={boxRef}
                className="relative flex min-w-0 flex-1 justify-end"
            >
                <AddressMeasure
                    address={destination.address}
                    measureRef={measureRef}
                />
                <Address
                    value={destination.address}
                    tiers={[tier]}
                    textVariant="small"
                    mono={false}
                    className={`${ADDRESS_VALUE_CLASS} ${
                        label ? "text-foreground-muted" : "text-foreground-base"
                    }`}
                />
            </span>
        </span>
    );
}

/**
 * "Add a new destination address", a plain option row with a plus where a
 * picked row shows its check (the DS item indicator's slot). Off while the
 * typed text is no Solana address; the reason is on demand, a tooltip on
 * the row and its accessible description, never a standing sentence.
 */
function AddDestinationOption({
    item,
    reason
}: {
    item: SavedDestination;
    reason: string | null;
}): React.ReactElement {
    const row = (
        <Text
            variant="small"
            render={<span />}
            className="min-w-0 flex-1 truncate"
        >
            {ADD_ROW_LABEL}
        </Text>
    );
    return (
        <ComboboxItem
            value={item}
            disabled={reason !== null}
            data-slot="combobox-add-item"
            aria-description={reason ?? undefined}
            // The reason's tooltip needs the pointer the DS takes from a
            // disabled row; the pointer must not light the row up.
            className="text-foreground-muted data-highlighted:text-foreground-base data-disabled:pointer-events-auto data-disabled:data-highlighted:bg-transparent data-disabled:data-highlighted:text-foreground-imprint"
        >
            {reason === null ? (
                row
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger
                            render={<span className="flex min-w-0 flex-1" />}
                        >
                            {row}
                        </TooltipTrigger>
                        <TooltipContent side="top">{reason}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
            {/* The DS item indicator's slot and size, so the plus sits
                exactly where a picked row's check does. */}
            <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
                <PlusIcon
                    aria-hidden
                    // why: 0.8125rem is the DS item indicator's check size.
                    className={`block size-[0.8125rem] transition-colors ${
                        reason === null
                            ? "text-foreground-neutral group-data-highlighted/combobox-item:text-accent-base"
                            : "text-foreground-imprint"
                    }`}
                />
            </span>
        </ComboboxItem>
    );
}

/**
 * Searchable picker over the Company Recipient's saved Rewards Destinations,
 * label inside like `AddressField`: the design system's `ComboboxInput` (a
 * lightbar `InputGroup`) with "Destination" in its inline-start
 * addon, per the DS Input prefix recipe. Full width: it fills the step's
 * content box, as step 1's Address field does.
 * Search matches a destination's label or its full address.
 *
 * Controlled by address: `value` is the chosen destination's address, "" for
 * the default. The first option, "Same as Position Source" (the source
 * address beside it), is that default: selected when nothing else is, and
 * what the clear (✕) returns to. An address that isn't a saved destination
 * (e.g. the Squads vault auto-fill, which is the default anyway) shows it too.
 *
 * Every option is one line, label at the left, address at the right
 * (`DestinationOption`). The field always shows the picked address,
 * shortened in the middle to the input's width (`useAddressFit`, rendered as
 * the DS `Address` would); its label is the Destination Label field below. What the user types to search is never shortened.
 *
 * With `onCreate`, a new address can be typed straight in: while the query
 * matches nothing (or is a valid new address), the list offers
 * "Add a new destination address" (`AddDestinationOption`). It commits only
 * when the query passes `checkTypedDestination` (a valid Solana address no
 * saved destination contains); otherwise it stays, disabled, with its reason
 * on demand. Committing it calls `onCreate(address)`; the owner persists it
 * and passes the address back as `draftAddress`, which the field shows
 * (read-only) while it saves and, with `error`, after a failed save.
 */
export function SavedDestinationCombobox({
    destinations,
    sourceAddress,
    sourceLabel = SAME_AS_SOURCE_LABEL,
    value,
    onChange,
    disabled = false,
    onCreate,
    draftAddress = null,
    creating = false,
    error = null,
    onRetry
}: {
    destinations: SavedDestination[];
    /** The position source: where rewards go by default. */
    sourceAddress: string;
    /** The default option's name: "Same as Position Source", or "Multisig
     *  Vault" when the source is a Squads multisig (its vault). */
    sourceLabel?: string;
    value: string;
    onChange: (address: string) => void;
    disabled?: boolean;
    /** Turns on typing a new address in: called with the trimmed address
     *  when the add row is committed. */
    onCreate?: (address: string) => void;
    /** A typed-in address being saved, or whose save failed: shown as the
     *  field's value, ahead of `value`. */
    draftAddress?: string | null;
    /** `draftAddress` is saving: the field goes inert. */
    creating?: boolean;
    /** Why the last save failed, inline under the field (no toast). */
    error?: string | null;
    /** Offered beside `error` when the save never reached the server. */
    onRetry?: () => void;
}): React.ReactElement {
    const inputRef = useRef<HTMLInputElement>(null);
    const fieldMeasureRef = useRef<HTMLSpanElement>(null);
    const source = useMemo(
        () => sourceDestination(sourceAddress, sourceLabel),
        [sourceAddress, sourceLabel]
    );
    const listed = useMemo(
        () => [source, ...destinations],
        [source, destinations]
    );
    const selected: SavedDestination = draftAddress
        ? draftDestination(draftAddress)
        : (findSavedDestination(destinations, value) ?? source);
    const isDefault = selected.uuid === SOURCE_UUID;
    const [query, setQuery] = useState("");

    // The field always reads as the picked address, fitted to the input; its
    // label is the Destination Label field's job.
    const selectedAddress = selected.address;
    const fieldTier = useAddressFit(
        selectedAddress,
        inputRef,
        fieldMeasureRef,
        selectedAddress !== ""
    );
    const selectedText = selectedAddress
        ? formatAddressTier(selectedAddress, fieldTier)
        : savedDestinationDisplayName(selected);

    // The input is controlled (see `inputValue` below), so it follows the
    // selection itself, as Base UI would for an uncontrolled one: on a pick,
    // a clear, a change from outside, or a re-fit.
    useLayoutEffect(() => {
        setQuery(selectedText);
    }, [selected.uuid, selectedText]);

    const inert = disabled || creating;
    const trimmed = query.trim();
    const searching = trimmed !== "" && query !== selectedText;
    // Checked against the source too: the default already covers it.
    const typed = checkTypedDestination(listed, query);
    const showAddRow =
        onCreate !== undefined &&
        !inert &&
        searching &&
        (typed.addable ||
            !listed.some((d) => matchesDestinationQuery(d, query)));
    const addItem = useMemo(() => addDestination(trimmed), [trimmed]);
    // Only while shown: an unfiltered list (Base UI skips the filter while
    // the field holds the pick) must not list it.
    const items = showAddRow ? [...listed, addItem] : listed;

    // The heads-up while the typed address can be added, and after: the
    // picked (or saving) destination itself.
    const warnExchange =
        (showAddRow && typed.warnExchange) ||
        (!isDefault && looksLikeExchangeAddress(selected.address));

    return (
        <div className="flex flex-col">
            <Combobox<SavedDestination>
                items={items}
                value={selected}
                onValueChange={(next: SavedDestination | null) => {
                    if (next !== null && next.uuid === ADD_UUID) {
                        if (typed.addable) onCreate?.(trimmed);
                        return;
                    }
                    // A saving or failed add isn't a saved destination yet.
                    if (next !== null && isDraftDestination(next)) return;
                    // The clear, like the default option, is the source.
                    if (next === null || next.uuid === SOURCE_UUID) {
                        onChange("");
                        return;
                    }
                    onChange(next.address);
                }}
                inputValue={query}
                onInputValueChange={(next: string) => setQuery(next)}
                isItemEqualToValue={(a, b) => a.uuid === b.uuid}
                // The pick reads as the field does, so an unedited field
                // lists every option.
                itemToStringLabel={(d: SavedDestination) =>
                    d.uuid === selected.uuid
                        ? selectedText
                        : d.uuid === ADD_UUID
                          ? d.address
                          : savedDestinationDisplayName(d)
                }
                filter={(destination, search) =>
                    destination.uuid === ADD_UUID ||
                    matchesDestinationQuery(destination, search)
                }
                disabled={inert}
            >
                <ComboboxInput
                    ref={inputRef}
                    aria-label="Destination"
                    aria-busy={creating || undefined}
                    className="w-full"
                    showClear={!isDefault && !creating}
                    disabled={inert}
                    title={
                        selectedAddress && query === selectedText
                            ? selectedAddress
                            : undefined
                    }
                    inputClassName={`${VALUE_TYPE_CLASS} ${
                        selectedAddress ? ADDRESS_VALUE_CLASS : ""
                    } ${DISABLED_VALUE_CLASS}`}
                >
                    <InputGroupAddon align="inline-start">
                        <InputGroupText className={DESTINATION_PREFIX_CLASS}>
                            Destination
                        </InputGroupText>
                    </InputGroupAddon>
                    {selectedAddress ? (
                        <AddressMeasure
                            address={selectedAddress}
                            measureRef={fieldMeasureRef}
                        />
                    ) : null}
                </ComboboxInput>
                <ComboboxContent>
                    <ComboboxList>
                        {onCreate === undefined ? (
                            <ComboboxEmpty>
                                No destination matches that label or address.
                            </ComboboxEmpty>
                        ) : null}
                        <ComboboxCollection>
                            {(destination: SavedDestination) =>
                                destination.uuid === ADD_UUID ? (
                                    <AddDestinationOption
                                        key={ADD_UUID}
                                        item={destination}
                                        reason={
                                            typed.addable
                                                ? null
                                                : ADD_ROW_INVALID_REASON
                                        }
                                    />
                                ) : (
                                    <ComboboxItem
                                        key={destination.uuid}
                                        value={destination}
                                    >
                                        <DestinationOption
                                            destination={destination}
                                        />
                                    </ComboboxItem>
                                )
                            }
                        </ComboboxCollection>
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            {warnExchange && <ExchangeAddressWarning />}
            {creating && (
                <Text
                    variant="label"
                    role="status"
                    className="mt-1.5 text-foreground-muted"
                >
                    Adding destination…
                </Text>
            )}
            {error ? (
                <FormErrorRetry
                    className="mt-1.5"
                    error={error}
                    onRetry={onRetry}
                    busy={creating}
                />
            ) : null}
        </div>
    );
}
